/**
 * inphner: the average detail modal, opened from a stat pill. Shows the N
 * times with the trimmed ones in parentheses, their scrambles, current vs
 * best (with the solve range the best came from), and "Copy as text" in
 * csTimer's style.
 */
import { effective, trimCount, trimmedIndices, DNF } from '../stats/core.ts';
import { syncStats } from '../stats/live.ts';
import { currentSession, currentSolves } from '../store.ts';
import { prefs } from '../prefs.ts';
import type { Solve } from '../types.ts';
import { esc } from '../ui/dom.ts';
import { openModal } from '../ui/modal.ts';
import { toast } from '../ui/toast.ts';
import { fmtSingle, fmtStat } from './solve-list.ts';
import { openSolveModal } from './solve-modal.ts';

const label = (n: number) => (n < 5 ? `mean of ${n}` : `avg of ${n}`);

/** csTimer-style text for a window of solves. */
export function averageText(n: number, value: string, solves: Solve[], trimmed: Set<number>): string {
  const date = new Date();
  const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const lines = solves.map((s, i) => {
    const t = fmtSingle(s);
    return `${i + 1}. ${trimmed.has(i) ? `(${t})` : t}   ${s.scramble}${s.comment ? `   [${s.comment}]` : ''}`;
  });
  return `Generated by inphner on ${day}\n${label(n)}: ${value}\n\nTime List:\n${lines.join('\n')}\n`;
}

export function openAverageModal(n: number, which: 'current' | 'best' = 'current'): void {
  const session = currentSession();
  const solves = currentSolves();
  const { stats } = syncStats(session.id, solves);
  const best = stats.best(n);
  const current = stats.current(n);
  if (solves.length < n) {
    toast(`${label(n)} needs ${n} solves (${solves.length} so far).`);
    return;
  }
  const show = which === 'best' && best ? best : { value: current, start: solves.length - n, end: solves.length };
  const windowSolves = solves.slice(show.start, show.end);
  const values = windowSolves.map((s) => effective(s, prefs().precision));
  const trimmed = n >= 5 ? trimmedIndices(values) : new Set<number>();
  const valueText = show.value === DNF ? 'DNF' : fmtStat(show.value, session.event);
  const rows = windowSolves.map((s, i) => `
    <button type="button" class="avg-row" data-solve="${esc(s.id)}" data-index="${show.start + i + 1}">
      <span class="c-idx num">${show.start + i + 1}</span>
      <span class="num avg-time ${trimmed.has(i) ? 'is-trimmed' : ''}">${trimmed.has(i) ? `(${esc(fmtSingle(s))})` : esc(fmtSingle(s))}</span>
      <span class="mono avg-scramble">${esc(s.scramble)}</span>
    </button>`).join('');

  const { root, close } = openModal({
    title: `${label(n)}`,
    size: 'lg',
    confirmLabel: 'Done',
    cancelLabel: '',
    bodyHtml: `
      <div class="avg-top">
        <div class="solve-detail-time num">${esc(valueText)}</div>
        ${best ? `<div class="segmented" role="group" aria-label="Which average">
          <button type="button" data-which="current" class="${which === 'current' ? 'on' : ''}">Current</button>
          <button type="button" data-which="best" class="${which === 'best' ? 'on' : ''}">Best ${esc(fmtStat(best.value, session.event))}</button>
        </div>` : ''}
      </div>
      <p class="text-dim avg-range">Solves ${show.start + 1}–${show.end} of ${solves.length}${n >= 5 ? ` · ${trimCount(n)} best and ${trimCount(n)} worst in parentheses` : ''}</p>
      <div class="avg-list">${rows}</div>
      <div class="solve-detail-actions"><button type="button" class="btn btn-sm" data-copy>Copy as text</button></div>`,
  });
  root.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const w = t.closest<HTMLElement>('[data-which]');
    if (w) {
      close();
      openAverageModal(n, w.dataset.which as 'current' | 'best');
      return;
    }
    const row = t.closest<HTMLElement>('[data-solve]');
    if (row) {
      close();
      openSolveModal(row.dataset.solve!, Number(row.dataset.index));
      return;
    }
    if (t.closest('[data-copy]')) {
      navigator.clipboard.writeText(averageText(n, valueText, windowSolves, trimmed)).then(
        () => toast('Copied.', { kind: 'good' }),
        () => toast('Copy failed: your browser blocked the clipboard.', { kind: 'bad' }),
      );
    }
  });
}
